import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MetaHead } from '../components/MetaHead';
import { COMPANY_INFO } from '../data/scrapedData';

export function BotilbudPage() {
  const criteriaPoints = [
    {
      number: "01",
      title: "Botilbud og/eller behandling",
      desc: "Om der er behov for botilbud alene eller sammen med misbrugsbehandling",
      icon: "fa-solid fa-house-chimney-medical"
    },
    {
      number: "02",
      title: "Helhed & sårbarhed",
      desc: "Hvilke psykiske, sociale og misbrugsrelaterede forhold der skal tages højde for",
      icon: "fa-solid fa-brain"
    },
    {
      number: "03",
      title: "Realistiske mål",
      desc: "Hvilke mål der er realistiske for borgeren og det samlede forløb",
      icon: "fa-solid fa-bullseye"
    },
    {
      number: "04",
      title: "Plan for videre forløb",
      desc: "Hvordan opfølgning, samarbejde og en senere overgang kan planlægges",
      icon: "fa-solid fa-route"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Botilbud ved misbrug, få trygge rammer | Midtgaarden",
    "description": "Få et trygt botilbud ved misbrug og psykisk sårbarhed i Sejstrup. Midtgaarden tilbyder individuelt tilrettelagt støtte, behandling og en sikker vej videre.",
    "publisher": {
      "@type": "Organization",
      "name": "Den Selvejende Institution Midtgaarden",
      "url": "https://www.midtgaarden.dk"
    }
  };

  return (
    <>
      <MetaHead
        title="Botilbud ved misbrug, få trygge rammer | Midtgaarden"
        description="Få et trygt botilbud ved misbrug og psykisk sårbarhed i Sejstrup. Midtgaarden tilbyder individuelt tilrettelagt støtte, behandling og en sikker vej videre."
        ogImage="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/kvinde-i-forklaede-laver-mad-i-storkoekken.jpg"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-[#1C1C1E] text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/kvinde-i-forklaede-laver-mad-i-storkoekken.jpg"
            alt="Kvinde i forklæde laver mad ved et stort komfur i et storkøkken"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1E] via-[#1C1C1E]/90 to-[#1C1C1E]/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 [text-shadow:_0_2px_12px_rgba(0,0,0,0.85)]">
          <div className="max-w-3xl space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
                <i className="fa-solid fa-house-chimney text-[11px]" aria-hidden="true" />
                Botilbud
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/90 backdrop-blur-xs">
                Midlertidigt botilbud & støtte
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Botilbud med helhedsorienteret støtte ved misbrug
            </h1>

            <div className="space-y-4 text-base sm:text-lg md:text-xl text-white/90 leading-relaxed font-normal">
              <p>
                Hos Midtgaarden tilbyder vi botilbud til mennesker, som har brug for trygge rammer omkring misbrug, psykisk sårbarhed og sociale udfordringer. Fra Sejstrup nær Esbjerg tilrettelægger vi opholdet ud fra den enkeltes situation, mål og behov for behandling og støtte.
              </p>
            </div>

            {/* CTA action buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href={COMPANY_INFO.phoneTel}
                className="h-14 px-8 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] active:bg-[#1F557B] text-white font-bold text-base shadow-lg transition-all duration-200 inline-flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8]"
              >
                <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href={COMPANY_INFO.emailMailto}
                aria-label="Send e-mail til Midtgaarden"
                className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white shrink-0"
              >
                <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
              </a>
            </div>

            {/* Trust highlights */}
            <div className="pt-6 flex flex-wrap justify-between items-center gap-y-3 border-t border-white/15 text-xs sm:text-sm text-white/80">
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>20+ års erfaring</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>Rolige rammer i Sejstrup</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>Socialpsykiatrisk støtte</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                <span>Overgang til egen bolig</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1: HVORNÅR KAN ET MIDLERTIDIGT BOTILBUD SKABE RO? */}
      <section id="hvordan-virker-det" className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Left column: Indhold & underafsnit */}
            <div className="lg:col-span-7 space-y-10">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  TRYGHED & STABILITET
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Hvornår kan et midlertidigt botilbud skabe ro?
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
                <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                  <p>
                    Et midlertidigt botilbud kan være relevant, når det er svært at skabe stabilitet i egen bolig, eller når overgangen fra behandling kræver tættere rammer. Formålet er at give ro til at arbejde med hverdagen og opbygge færdigheder, der understøtter næste skridt.
                  </p>
                  <p>
                    Midlertidigt betyder ikke, at alle følger samme plan. Opholdets mål, indhold og forventede varighed afklares individuelt og i dialog med relevante samarbejdspartnere. Vi ser på både misbrug, psykisk trivsel, relationer og den sociale situation.
                  </p>
                </div>
              </div>

              {/* Subsection: Når du søger et socialpsykiatrisk botilbud */}
              <div className="pt-8 border-t border-[#1C1C1E]/10 space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                    SOCIALPSYKIATRI & HELHED
                  </span>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C1C1E] tracking-tight mb-4">
                    Når du søger et socialpsykiatrisk botilbud
                  </h3>
                  <div className="w-16 h-1 bg-[#3885B8] my-4" />
                </div>

                <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed">
                  <p>
                    Når man søger efter et botilbud til psykisk syge, handler behovet ofte om mere end en bolig. Hos os møder vi mennesket før diagnosen. Vi tager højde for psykisk sårbarhed, et aktuelt eller tidligere misbrug og de forhold, der påvirker muligheden for en stabil hverdag.
                  </p>
                  <p>
                    For dig, der søger et socialpsykiatrisk botilbud, kan Midtgaardens helhedsorienterede tilgang være relevant. Vi arbejder ud fra en forståelse, hvor biologiske, psykiske og sociale forhold ses i sammenhæng, også når der er tale om{' '}
                    <Link
                      to="/dobbeltdiagnose"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      dobbeltdiagnose
                    </Link>
                    .
                  </p>
                </div>

                <div className="pt-2">
                  <Link
                    to="/dobbeltdiagnose"
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    <span>Læs mere om behandling af dobbeltdiagnose</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Right column: Sticky infoboks & billede */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
              <div className="bg-[#F8FAFC] rounded-none border border-[#1C1C1E]/10 p-6 sm:p-8 shadow-xs space-y-5">
                <div className="flex items-center gap-3 border-b border-[#1C1C1E]/10 pb-4">
                  <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center font-bold text-base shrink-0">
                    <i className="fa-solid fa-hands-holding-child" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-[#1C1C1E]">
                      Mennesket før diagnosen
                    </h4>
                    <p className="text-xs text-[#1C1C1E]/60">Midtgaardens faglige tilgang</p>
                  </div>
                </div>

                <div className="space-y-3.5 text-sm text-[#1C1C1E]/80">
                  <div className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                    <span><strong>Faste rammer:</strong> Struktur, trygge fællesarealer og egen boligenhed.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                    <span><strong>Helhedssyn:</strong> Biologiske, psykologiske og sociale vilkår tænkes sammen.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                    <span><strong>Individuelt tempo:</strong> Opholdets længde og mål tilpasses borgeren og handleplanen.</span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                    <span><strong>Samarbejde:</strong> Tæt dialog med kommunale sagsbehandlere og pårørende.</span>
                  </div>
                </div>

                <div className="pt-2 border-t border-[#1C1C1E]/10">
                  <Link
                    to="/paragraf-101-og-paragraf-141"
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    <span>Læs om § 101 og § 141 handleplaner</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              {/* Photo from Midtgaarden Sejstrup */}
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/hvidmalet-bygning-med-graesplaene-og-traegavl.jpg"
                  alt="Hvidmalet bygning med græsplæne og gavl i gråt træværk under blå himmel"
                  className="w-full h-56 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                  <p className="text-xs text-[#1C1C1E]/70 flex items-start gap-2">
                    <i className="fa-solid fa-location-dot text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span>Midtgaarden i Sejstrup nær Esbjerg tilbyder rolige, naturrige omgivelser med plads til at genfinde hverdagens rytme.</span>
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 2: BOTILBUD VED MISBRUG MED BLIK FOR HELE MENNESKET & FORSKEL PÅ BOTILBUD OG DØGNBEHANDLING */}
      <section className="py-16 sm:py-24 bg-[#F8FAFC] border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              HELHEDSORIENTERET INDSATS
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Botilbud ved misbrug med blik for hele mennesket
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              <p>
                Et botilbud ved misbrug skal skabe rammer, hvor forandring kan blive en del af hverdagen. Hos Midtgaarden kan opholdet indgå i et individuelt forløb med{' '}
                <Link
                  to="/behandling"
                  className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                >
                  misbrugsbehandling og døgnbehandling
                </Link>
                , dagbehandling eller efterværn, når det er fagligt relevant.
              </p>
              <p>
                Den konkrete støtte afhænger af borgerens behov og det aftalte forløb. I Sejstrup er der boafdelinger, fællesrum og fritidsområder, som danner ramme om opholdet. Vi lægger vægt på balance mellem fællesskab, personlig udvikling og respekt for den enkelte.
              </p>
            </div>
          </div>

          {/* Underafsnit: Hvad er forskellen på botilbud og døgnbehandling? */}
          <div className="pt-6 border-t border-[#1C1C1E]/10 space-y-8">
            <div className="max-w-3xl space-y-3">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                BEGREBSAFKLARING
              </span>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C1C1E] tracking-tight">
                Hvad er forskellen på botilbud og døgnbehandling?
              </h3>
              <div className="w-16 h-1 bg-[#3885B8] my-4" />
              <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                <p>
                  Et botilbud giver en boligramme med støtte omkring hverdagen, mens døgnbehandling er et mere intensivt behandlingsforløb, hvor borgeren bor på behandlingsstedet. Behovet afgør, om løsningen er et botilbud, behandling eller et sammenhængende forløb med flere dele.
                </p>
                <p>
                  Derfor tager vi ikke udgangspunkt i en fast model for alle. Nogle har især brug for et stabilt sted at bo, mens andre har behov for forudgående{' '}
                  <Link
                    to="/behandling/afgiftning"
                    className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    afgiftning
                  </Link>{' '}
                  eller samtidig behandling af misbrug og psykiske problemstillinger. Vi afklarer sammen, hvilken ramme der giver faglig mening.
                </p>
              </div>
            </div>

            {/* Comparison Cards: Botilbud vs Døgnbehandling */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4">
              
              {/* Card 1: Botilbud */}
              <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 shadow-xs flex flex-col justify-between">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-house-chimney" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8]">
                        Boligramme med støtte
                      </span>
                      <h4 className="text-xl sm:text-2xl font-bold text-[#1C1C1E]">
                        Botilbud hos Midtgaarden
                      </h4>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                    <p>
                      Målrettet borgere, som primært har behov for en tryg og stabil bolig med pædagogisk støtte i dagligdagen for at opbygge en velfungerende hverdag.
                    </p>
                    <ul className="space-y-2 pt-2 text-sm text-[#1C1C1E]/85">
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                        <span>Fokus på daglige rutiner, botræning og trivsel</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                        <span>Fællesskab, fritidstilbud og relationer</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#3885B8] mt-1 shrink-0 text-xs" aria-hidden="true" />
                        <span>Kan kombineres med misbrugsbehandling ved behov</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="pt-8 mt-6 border-t border-[#1C1C1E]/10 flex flex-wrap items-center justify-between gap-4">
                  <a
                    href="#afklaring-forhoer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#3885B8] text-white text-xs sm:text-sm font-bold hover:bg-[#2A6E9B] transition-colors"
                  >
                    <span>Afklar botilbudsplads</span>
                    <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                  </a>
                  <Link
                    to="/midtgaarden-i-sejstrup"
                    className="text-xs font-semibold text-[#1C1C1E]/70 hover:text-[#3885B8] transition-colors"
                  >
                    Se rammerne i Sejstrup →
                  </Link>
                </div>
              </div>

              {/* Card 2: Døgnbehandling */}
              <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 shadow-xs flex flex-col justify-between">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-none bg-[#B86B43]/10 text-[#B86B43] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-bed" aria-hidden="true" />
                    </div>
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#B86B43]">
                        Intensiv misbrugsindsats
                      </span>
                      <h4 className="text-xl sm:text-2xl font-bold text-[#1C1C1E]">
                        Døgnbehandling for misbrug
                      </h4>
                    </div>
                  </div>

                  <div className="space-y-3 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                    <p>
                      Målrettet borgere, som har brug for en intensiv, terapeutisk misbrugsbehandling i døgnregi med fuld afstand til rusmidler og hverdagens risikofaktorer.
                    </p>
                    <ul className="space-y-2 pt-2 text-sm text-[#1C1C1E]/85">
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#B86B43] mt-1 shrink-0 text-xs" aria-hidden="true" />
                        <span>Afgiftning, primærbehandling og Cenaps-modellen</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#B86B43] mt-1 shrink-0 text-xs" aria-hidden="true" />
                        <span>24/7 sundhedsfaglig og pædagogisk bemanding</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="fa-solid fa-check text-[#B86B43] mt-1 shrink-0 text-xs" aria-hidden="true" />
                        <span>Struktureret terapi, grupper og tilbagefaldsforebyggelse</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="pt-8 mt-6 border-t border-[#1C1C1E]/10 flex flex-wrap items-center justify-between gap-4">
                  <Link
                    to="/doegnbehandling"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1C1C1E] text-white text-xs sm:text-sm font-bold hover:bg-[#3885B8] transition-colors"
                  >
                    <span>Læs mere om døgnbehandling</span>
                    <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/priser"
                    className="text-xs font-semibold text-[#1C1C1E]/70 hover:text-[#3885B8] transition-colors"
                  >
                    Se takster og priser →
                  </Link>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* SECTION 3: VISUAL BREAK / FÆLLESSKAB & OMGIVELSER */}
      <section className="relative overflow-hidden min-h-[380px] sm:min-h-[460px] md:min-h-[500px] flex items-end bg-[#1C1C1E]">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/to-unge-maend-spiller-pool-i-aktivitetsrum.jpg"
          alt="To unge mænd spiller pool ved et grønt billardbord i et aktivitetsrum"
          className="absolute inset-0 w-full h-full object-cover object-center brightness-95 contrast-[1.03] rounded-none"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/95 via-[#1C1C1E]/45 to-[#1C1C1E]/20" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 text-white">
          <div className="max-w-2xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
              <i className="fa-solid fa-heart text-[11px]" aria-hidden="true" />
              Fællesskab & hverdagsliv
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
              Et miljø, hvor forandring kan blive en del af hverdagen
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl">
              I Sejstrup er der boafdelinger, fællesrum og fritidsområder, der skaber balance mellem fællesskab, personlig udvikling og respekt for den enkelte.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4: EN TRYG OVERGANG TIL EN MERE SELVSTÆNDIG HVERDAG */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            {/* Visual element / photo */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/maend-sidder-samlet-i-samtale-ved-bord.jpg"
                  alt="Mænd sidder samlet og taler sammen ved et bord i et lyst lokale"
                  className="w-full h-56 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10">
                  <p className="text-xs text-[#1C1C1E]/70 flex items-start gap-2">
                    <i className="fa-solid fa-house-user text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span>Halvvejshuset i Esbjerg tilbyder en tryg og gradvis overgang fra behandling til en selvstændig tilværelse.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Prose content */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  UDSLUSNING & EFTERVÆRN
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  En tryg overgang til en mere selvstændig hverdag
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
              </div>

              <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                <p>
                  Når en behandling nærmer sig sin afslutning, kan overgangen til en mere selvstændig hverdag være sårbar. Midtgaardens halvvejshus i Esbjerg kan indgå i udslusning og efterværn, så næste skridt bliver planlagt med blik for bolig, netværk og fortsat støtte.
                </p>
                <p>
                  Vi ser udslusning som en vigtig del af det samlede forløb. Målet aftales med borgeren og relevante samarbejdspartnere, så støtten passer til den aktuelle situation. Planen kan justeres, hvis behov eller forudsætninger ændrer sig undervejs.
                </p>
              </div>

              {/* Three key pillars */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="bg-[#F8FAFC] border border-[#1C1C1E]/10 p-4 rounded-none space-y-1 shadow-2xs">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm mb-2">
                    <i className="fa-solid fa-hand-holding-hand" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-sm text-[#1C1C1E]">1. Fortsat støtte</h4>
                  <p className="text-xs text-[#1C1C1E]/70">Faglig sparring og tryghed i den sårbare overgangsfase.</p>
                </div>

                <div className="bg-[#F8FAFC] border border-[#1C1C1E]/10 p-4 rounded-none space-y-1 shadow-2xs">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm mb-2">
                    <i className="fa-solid fa-network-wired" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-sm text-[#1C1C1E]">2. Netværk & bolig</h4>
                  <p className="text-xs text-[#1C1C1E]/70">Planlægning af fremtidig boligsituation og sunde sociale relationer.</p>
                </div>

                <div className="bg-[#F8FAFC] border border-[#1C1C1E]/10 p-4 rounded-none space-y-1 shadow-2xs">
                  <div className="w-8 h-8 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm mb-2">
                    <i className="fa-solid fa-arrows-split-up-and-left" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-sm text-[#1C1C1E]">3. Tilpasset plan</h4>
                  <p className="text-xs text-[#1C1C1E]/70">Løbende justering ud fra borgerens udvikling og behov.</p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/halvvejshuset-i-esbjerg"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                >
                  <span>Læs mere om Halvvejshuset i Esbjerg</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 5: HVAD AFKLARER VI FØR ET OPHOLD? */}
      <section className="py-16 sm:py-24 bg-[#F8FAFC] border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="max-w-3xl space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              INDLEDENDE AFKLARING
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Hvad afklarer vi før et ophold?
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              <p>
                Det rette match er vigtigt, fordi et botilbud ved misbrug og psykisk sårbarhed skal kunne rumme den samlede situation. Før et muligt ophold taler vi om aktuelle udfordringer, tidligere forløb, ressourcer og mål samt behovet for samarbejde med kommune eller myndighed.
              </p>
            </div>
          </div>

          {/* Grid of the 4 List Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {criteriaPoints.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#1C1C1E]/10 rounded-none p-6 flex flex-col justify-between shadow-xs hover:shadow-md hover:border-[#3885B8]/50 transition-all duration-200"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-extrabold text-[#3885B8]/30 font-mono">
                      {item.number}
                    </span>
                    <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-sm">
                      <i className={item.icon} aria-hidden="true" />
                    </div>
                  </div>

                  <h3 className="font-bold text-lg text-[#1C1C1E] leading-snug">
                    {item.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-[#1C1C1E]/5 mt-4">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#3885B8]">
                    <i className="fa-solid fa-check text-[11px]" aria-hidden="true" />
                    Faglig vurdering
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing & dialogue information */}
          <div className="max-w-3xl pt-2">
            <p className="text-base text-[#1C1C1E]/80 leading-relaxed">
              Pris, bevilling og de praktiske rammer afhænger af det konkrete forløb og den aftale, der ligger til grund for opholdet. Kontakt os for en faglig afklaring. Vi svarer også gerne på spørgsmål fra pårørende, kommuner og andre fagpersoner.
            </p>
          </div>

        </div>
      </section>

      {/* SECTION 6: BOTILBUD I SEJSTRUP & FÅ EN FAGLIG SAMTALE */}
      <section className="py-16 sm:py-20 bg-[#F8FAFC] border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-12 shadow-xs">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8 space-y-4">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  VISITATION & KONTAKT
                </span>
                
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Botilbud i Sejstrup for borgere fra hele landet
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
                
                <div className="space-y-3 text-base text-[#1C1C1E]/80 leading-relaxed max-w-2xl pt-1">
                  <p>
                    Midtgaarden har mere end 20 års erfaring som center for{' '}
                    <Link
                      to="/"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      misbrugsbehandling
                    </Link>{' '}
                    og komplekse problemstillinger. Vores botilbud ligger i rolige rammer i Sejstrup nær Esbjerg, og vi modtager henvendelser fra borgere, pårørende og fagpersoner i hele landet.
                  </p>
                  <p>
                    Er du i tvivl om, hvorvidt et midlertidigt botilbud eller et andet tilbud passer til situationen, er du velkommen til at kontakte os. Sammen afklarer vi behovet og ser på, om Midtgaarden kan være den rette ramme for det videre forløb.
                  </p>
                </div>

                <div className="pt-4 flex flex-wrap items-center gap-3">
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#3885B8] text-white text-sm font-bold hover:bg-[#2A6E9B] transition-colors shadow-xs"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    <span>Ring til os på {COMPANY_INFO.phone}</span>
                  </a>
                  <a
                    href={COMPANY_INFO.emailMailto}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#1C1C1E]/20 hover:border-[#3885B8] bg-white text-[#1C1C1E] hover:text-[#3885B8] text-sm font-semibold transition-colors"
                  >
                    <i className="fa-solid fa-envelope text-[#3885B8]" aria-hidden="true" />
                    <span>Send en e-mail</span>
                  </a>
                </div>
              </div>

              {/* Right box: Quick facts / links */}
              <div className="lg:col-span-4 bg-[#F8FAFC] p-6 sm:p-8 rounded-none border border-[#1C1C1E]/10 space-y-4">
                <h3 className="font-bold text-base text-[#1C1C1E]">
                  Hvem henvender sig?
                </h3>
                <ul className="space-y-2.5 text-xs sm:text-sm text-[#1C1C1E]/80">
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-user-check text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span><strong>Borgere:</strong> Få uforpligtende rådgivning om mulighederne for hjælp og botilbud.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-heart text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span><strong>Pårørende:</strong> Få information om trygge rammer og støtte i Sejstrup.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-briefcase text-[#3885B8] mt-0.5 shrink-0" aria-hidden="true" />
                    <span><strong>Fagpersoner & kommuner:</strong> Drøft visitation, takster, § 107/§ 101 og handleplaner.</span>
                  </li>
                </ul>

                <div className="pt-2 border-t border-[#1C1C1E]/10 flex flex-col gap-2 text-xs font-bold text-[#3885B8]">
                  <Link to="/paragraf-101-og-paragraf-141" className="hover:underline flex items-center justify-between text-[#1C1C1E] hover:text-[#3885B8]">
                    <span className="flex items-center gap-1.5">
                      <i className="fa-solid fa-scale-balanced text-[#3885B8] text-[11px]" aria-hidden="true" />
                      <strong>Læs om § 101 og § 141</strong>
                    </span>
                    <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                  </Link>
                  <Link to="/fagperson" className="hover:underline flex items-center justify-between">
                    <span>Information til fagpersoner</span>
                    <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                  </Link>
                  <Link to="/midtgaarden-i-sejstrup" className="hover:underline flex items-center justify-between">
                    <span>Om Midtgaarden i Sejstrup</span>
                    <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                  </Link>
                  <Link to="/priser" className="hover:underline flex items-center justify-between">
                    <span>Se takster og priser</span>
                    <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
}
